import { io } from 'socket.io-client';
import { authApi } from './api';

// Get WebSocket URL from API URL (same host, different protocol for WS)
const getWebSocketUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    // Remove /api suffix and return base URL
    return apiUrl.replace('/api', '');
};

class WebSocketService {
    constructor() {
        this.socket = null;
        this.currentOrderId = null;
        this.listeners = new Map();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
    }

    /**
     * Connect to the WebSocket server
     * @returns {Promise<boolean>} True if connected successfully
     */
    connect() {
        return new Promise((resolve, reject) => {
            const token = authApi.getToken();

            if (!token) {
                console.warn('WebSocket: No auth token available');
                resolve(false);
                return;
            }

            if (this.socket?.connected) {
                console.log('WebSocket: Already connected');
                resolve(true);
                return;
            }

            const wsUrl = getWebSocketUrl();
            console.log('WebSocket: Connecting to', wsUrl);

            this.socket = io(wsUrl, {
                auth: { token },
                transports: ['websocket', 'polling'],
                reconnection: true,
                reconnectionAttempts: this.maxReconnectAttempts,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
            });

            this.socket.on('connect', () => {
                console.log('WebSocket: Connected');
                this.reconnectAttempts = 0;

                // Rejoin order room if we were in one
                if (this.currentOrderId) {
                    this.joinOrder(this.currentOrderId);
                }

                resolve(true);
            });

            this.socket.on('connect_error', (error) => {
                console.error('WebSocket: Connection error', error.message);
                this.reconnectAttempts++;

                if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                    reject(new Error('WebSocket connection failed'));
                }
            });

            this.socket.on('disconnect', (reason) => {
                console.log('WebSocket: Disconnected', reason);
            });

            // Set up event listeners
            this.socket.on('new-message', (message) => {
                this.emit('new-message', message);
            });

            this.socket.on('order-updated', (order) => {
                this.emit('order-updated', order);
            });
        });
    }

    /**
     * Disconnect from the WebSocket server
     */
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.currentOrderId = null;
            console.log('WebSocket: Disconnected manually');
        }
    }

    /**
     * Check if connected
     * @returns {boolean}
     */
    isConnected() {
        return this.socket?.connected || false;
    }

    /**
     * Join an order room to receive updates for that order
     * @param {number} orderId - The order ID to join
     */
    joinOrder(orderId) {
        if (!this.socket?.connected) {
            console.warn('WebSocket: Cannot join order - not connected');
            // Store the orderId to join when connected
            this.currentOrderId = orderId;
            return;
        }

        // Leave current room if different
        if (this.currentOrderId && this.currentOrderId !== orderId) {
            this.leaveOrder(this.currentOrderId);
        }

        this.currentOrderId = orderId;
        this.socket.emit('join-order', orderId);
        console.log('WebSocket: Joined order room', orderId);
    }

    /**
     * Leave an order room
     * @param {number} orderId - The order ID to leave
     */
    leaveOrder(orderId) {
        if (!this.socket?.connected) {
            return;
        }

        this.socket.emit('leave-order', orderId);

        if (this.currentOrderId === orderId) {
            this.currentOrderId = null;
        }

        console.log('WebSocket: Left order room', orderId);
    }

    /**
     * Add an event listener
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     */
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(callback);
    }

    /**
     * Remove an event listener
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     */
    off(event, callback) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).delete(callback);
        }
    }

    /**
     * Emit an event to all registered listeners
     * @param {string} event - Event name
     * @param {any} data - Event data
     */
    emit(event, data) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error('WebSocket: Error in listener callback', error);
                }
            });
        }
    }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;
